import { SelectableUserRole } from '~/gen/proto/bff/v1/common_pb'
import { LicenseStatus } from '~/gen/proto/bff/v1/model_pb'

// An active user role configured on an effective contract plan.
export type EffectivePlanRole = {
  code: string
  name: string
  engName: string
}

// A plan whose plans/roles appear in the member filter and in the CSV plan
// columns.
export type EffectivePlan = {
  planCode: string
  planName: string
  engPlanName: string
  roles: EffectivePlanRole[]
}

// The organization's currently effective base contract plans, deduped by
// plan code in plan-id order. This single list defines which plans and roles
// appear in the member filter and in the CSV plan columns.
// - ListSelectableUserRoles already returns only base plans and active roles.
// - Not-yet-started contracts (LICENSE_EFFECTIVE_IN_FUTURE) are excluded:
//   only contracts whose license period is active now are kept.
// - Plans whose user roles are not displayed to the organization
//   (ユーザーロール表示設定が表示しない, or a non-public product) are
//   excluded, matching the member detail page's visibility rule.
export function effectivePlans(
  selectableUserRoles: SelectableUserRole[],
): EffectivePlan[] {
  const seen = new Set<string>()
  const plans: EffectivePlan[] = []
  for (const selectable of selectableUserRoles) {
    const license = selectable.license
    if (license === undefined) {
      continue
    }
    if (license.status !== LicenseStatus.LICENSE_ACTIVE) {
      continue
    }
    if (!license.isUserRoleConfigurable || !license.isProductPublic) {
      continue
    }
    if (seen.has(license.planCode)) {
      continue
    }
    seen.add(license.planCode)
    plans.push({
      planCode: license.planCode,
      planName: license.planName,
      engPlanName: license.engPlanName,
      roles: selectable.userRoleSetting.map((setting) => ({
        code: setting.userRoleCode,
        name: setting.userRoleName,
        engName: setting.userRoleEngName,
      })),
    })
  }
  return plans
}

// Roles selectable in the member filter: the roles of the selected plans, or
// of all effective plans when no plan is selected. Deduped by role name
// (same-named roles across plans are offered as a single option), keeping
// the first appearance order.
export function availableRoles(
  plans: EffectivePlan[],
  selectedPlanCodes: string[],
): EffectivePlanRole[] {
  const targets = plans.filter(
    (plan) =>
      selectedPlanCodes.length === 0 ||
      selectedPlanCodes.includes(plan.planCode),
  )
  const byName = new Map<string, EffectivePlanRole>()
  for (const plan of targets) {
    for (const role of plan.roles) {
      const seen = byName.get(role.name)
      if (seen === undefined) {
        byName.set(role.name, { ...role })
      } else if (seen.engName === '' && role.engName !== '') {
        // engName can be empty per plan; backfill it from another plan's
        // copy of the same-named role so the English label survives dedup.
        seen.engName = role.engName
      }
    }
  }
  return Array.from(byName.values())
}
